import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { INCIDENT_TEXT_MAX_LENGTH } from '../../services/IncidentService';
import { parseDateTimeLocalValue, toDateTimeLocalValue } from '../../utils/DiactiveUtils';

const defaultDateTimeLocal = () => toDateTimeLocalValue(new Date());

const IncidentFormDialog = ({
  open,
  onClose,
  onSubmit,
  initialText = '',
  initialCreatedAt = null,
  title,
  submitLabel,
}) => {
  const [text, setText] = useState('');
  const [createdAtLocal, setCreatedAtLocal] = useState(defaultDateTimeLocal);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(initialText ?? '');
    setCreatedAtLocal(
      initialCreatedAt ? toDateTimeLocalValue(initialCreatedAt) : defaultDateTimeLocal()
    );
    setError(null);
    setSubmitting(false);
  }, [open, initialText, initialCreatedAt]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError('El texto es obligatorio');
      return;
    }
    if (trimmed.length > INCIDENT_TEXT_MAX_LENGTH) {
      setError(`Máximo ${INCIDENT_TEXT_MAX_LENGTH} caracteres`);
      return;
    }
    const createdAt = parseDateTimeLocalValue(createdAtLocal);
    if (!createdAt) {
      setError('La fecha y hora no son válidas');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ text: trimmed, createdAt });
      setSubmitting(false);
      onClose();
    } catch (e) {
      setError(e.message || 'Error al guardar');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label="Fecha y hora"
          name="createdAt"
          type="datetime-local"
          value={createdAtLocal}
          onChange={(e) => setCreatedAtLocal(e.target.value)}
          fullWidth
          margin="normal"
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Descripción"
          name="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          margin="normal"
          required
          multiline
          minRows={4}
          inputProps={{ maxLength: INCIDENT_TEXT_MAX_LENGTH }}
          helperText={`${text.length}/${INCIDENT_TEXT_MAX_LENGTH}`}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentFormDialog;
